import { useState } from "react";
import { Clock, Mail, MapPin, Phone } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "../components/motion-primitives";
import { CONTACT, SERVICE_OPTIONS } from "../data/content";

const OFFICE = [
  { icon: MapPin, text: CONTACT.officeAddress, testid: "contact-address" },
  { icon: Phone, text: CONTACT.phone, href: CONTACT.phoneHref, testid: "contact-phone" },
  { icon: Mail, text: CONTACT.email, href: `mailto:${CONTACT.email}`, testid: "contact-email" },
  { icon: Clock, text: CONTACT.hours, testid: "contact-hours" },
];

const EMPTY = {
  fullName: "",
  email: "",
  phone: "",
  company: "",
  service: "",
  cargoDescription: "",
  origin: "",
  destination: "",
  additionalDetails: "",
};

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const res = await fetch("/api/quote-requests", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_name: form.fullName,
          email: form.email,
          phone: form.phone,
          company: form.company,
          service: form.service,
          cargo_description: form.cargoDescription,
          origin: form.origin,
          destination: form.destination,
          additional_details: form.additionalDetails,
        }),
      });
      if (!res.ok) throw new Error("failed");
      toast.success("Quote request received! Our team will get back to you within 24 hours.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong. Please call us at " + CONTACT.phone + ".");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* HERO */}
      <section className="bg-section py-20 text-center md:py-24" data-testid="contact-hero">
        <div className="container-x">
          <Reveal>
            <span className="eyebrow-pill">Get in Touch</span>
            <h1 className="mt-5 font-heading text-4xl font-extrabold tracking-tight md:text-5xl">
              Get in Touch
            </h1>
            <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-muted-foreground">
              Reach out to our team for freight quotes, shipment tracking, or
              any enquiries. We're here to help.
            </p>
          </Reveal>
        </div>
      </section>

      {/* OFFICE + FORM */}
      <section className="py-16 md:py-24" data-testid="contact-body">
        <div className="container-x grid gap-10 lg:grid-cols-2">
          <Reveal>
            <div className="h-full rounded-2xl border border-border bg-white p-8 shadow-sm" data-testid="head-office-card">
              <span className="eyebrow-pill">Head Office</span>
              <h2 className="mt-5 font-heading text-3xl font-extrabold tracking-tight">
                {CONTACT.officeCity}
              </h2>
              <ul className="mt-7 space-y-5">
                {OFFICE.map(({ icon: Icon, text, href, testid }) => (
                  <li key={testid} className="flex items-start gap-3 text-sm text-foreground/85">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#FEF0DC] text-brand">
                      <Icon className="h-4 w-4" />
                    </span>
                    {href ? (
                      <a href={href} data-testid={testid} className="mt-2 transition-colors hover:text-brand">
                        {text}
                      </a>
                    ) : (
                      <span className="mt-2" data-testid={testid}>{text}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          <Reveal delay={0.08}>
            <form
              onSubmit={submit}
              data-testid="quote-form"
              className="rounded-2xl border border-border bg-white p-8 shadow-sm"
            >
              <h2 className="font-heading text-2xl font-extrabold tracking-tight">
                Request a Freight Quote
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Fill in the details below and our team will get back to you
                within 24 hours.
              </p>

              <div className="mt-7 grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="fullName" className="field-label">Full Name *</label>
                  <input id="fullName" data-testid="quote-full-name" required value={form.fullName} onChange={set("fullName")} className="field-input" />
                </div>
                <div>
                  <label htmlFor="email" className="field-label">Email Address *</label>
                  <input id="email" type="email" data-testid="quote-email" required value={form.email} onChange={set("email")} className="field-input" />
                </div>
                <div>
                  <label htmlFor="phone" className="field-label">Phone Number</label>
                  <input id="phone" data-testid="quote-phone" value={form.phone} onChange={set("phone")} className="field-input" />
                </div>
                <div>
                  <label htmlFor="company" className="field-label">Company Name</label>
                  <input id="company" data-testid="quote-company" value={form.company} onChange={set("company")} className="field-input" />
                </div>
              </div>

              <p className="mt-7 text-xs font-bold uppercase tracking-[0.2em] text-brand">
                Shipment Details
              </p>
              <div className="mt-4 grid gap-5 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label htmlFor="service" className="field-label">Service Required *</label>
                  <select id="service" data-testid="quote-service" required value={form.service} onChange={set("service")} className="field-input">
                    <option value="">Select a service</option>
                    {SERVICE_OPTIONS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="cargo" className="field-label">Cargo Description</label>
                  <input id="cargo" data-testid="quote-cargo" value={form.cargoDescription} onChange={set("cargoDescription")} className="field-input" />
                </div>
                <div>
                  <label htmlFor="origin" className="field-label">Origin</label>
                  <input id="origin" data-testid="quote-origin" value={form.origin} onChange={set("origin")} className="field-input" />
                </div>
                <div>
                  <label htmlFor="destination" className="field-label">Destination</label>
                  <input id="destination" data-testid="quote-destination" value={form.destination} onChange={set("destination")} className="field-input" />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="details" className="field-label">Additional Details</label>
                  <textarea id="details" rows={4} data-testid="quote-details" value={form.additionalDetails} onChange={set("additionalDetails")} className="field-input" />
                </div>
              </div>

              <button
                type="submit"
                data-testid="quote-submit"
                disabled={loading}
                className="btn-primary mt-8 w-full disabled:opacity-60"
              >
                {loading ? "Submitting…" : "Submit Quote Request"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
